import { PresenceUser } from '@/types/presence';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@superfunnel/ui-core/v2/Tooltip';
import { classNames } from '@superfunnel/utils';

interface PresenceIndicatorProps {
  users: PresenceUser[];
  maxVisible?: number;
  size?: 'sm' | 'md' | 'lg';
}

const sizeClasses = {
  sm: 'h-6 w-6 text-xs',
  md: 'h-8 w-8 text-sm',
  lg: 'h-10 w-10 text-base',
};

export function PresenceIndicator({
  users,
  maxVisible = 5,
  size = 'md',
}: PresenceIndicatorProps) {
  const visibleUsers = users.slice(0, maxVisible);
  const remainingCount = Math.max(0, users.length - maxVisible);

  if (users.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center -space-x-2">
      {visibleUsers.map((user) => (
        <UserAvatar key={user.userId} user={user} size={size} />
      ))}
      {remainingCount > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={classNames(
                'flex items-center justify-center rounded-full border-2 border-white bg-gray-200 font-medium text-gray-600',
                sizeClasses[size],
              )}
            >
              +{remainingCount}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <div className="space-y-1">
              {users.slice(maxVisible).map((user) => (
                <div key={user.userId} className="text-sm">
                  {user.name}
                </div>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

interface UserAvatarProps {
  user: PresenceUser;
  size?: 'sm' | 'md' | 'lg';
}

function UserAvatar({ user, size = 'md' }: UserAvatarProps) {
  const initials = getInitials(user.name);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={classNames(
            'relative flex items-center justify-center rounded-full border-2 border-white ring-2 ring-white transition-transform hover:z-10 hover:scale-110',
            sizeClasses[size],
          )}
          style={{
            backgroundColor: user.avatar ? 'transparent' : user.color,
          }}
        >
          {user.avatar ? (
            <img
              src={user.avatar}
              alt={user.name}
              className="h-full w-full rounded-full object-cover"
            />
          ) : (
            <span className="font-semibold text-white">{initials}</span>
          )}

          {/* Active indicator */}
          <div className="absolute -right-0.5 -bottom-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-green-500" />
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <div className="space-y-0.5">
          <div className="font-medium">{user.name}</div>
          <div className="text-xs text-gray-400">
            {user.location === 'editor' ? 'Editing' : 'Viewing'}
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function getInitials(name: string): string {
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}
